import type { ConditionOption } from '../StringFilterEditor/StringFilterEditor'

type ExtraConditionOption = {
  label: string
  value: string
  isArray?: boolean
  requiresValue?: boolean
}

export const mergeAndSortConditionOptions = (
  schemaOptions: ConditionOption[],
  extraOptions: ExtraConditionOption[],
  order: string[],
  filterFn?: (option: ConditionOption) => boolean,
): ConditionOption[] => {
  const filtered = filterFn ? schemaOptions.filter(filterFn) : schemaOptions
  const existingKeys = new Set(filtered.map(option => option.value))
  const extras: ConditionOption[] = extraOptions
    .filter(option => !existingKeys.has(option.value))
    .map(({ label, value, isArray, requiresValue }) => ({
      label,
      value,
      isArray,
      requiresValue,
    }))
  const orderIndex = (key: string) => {
    const index = order.indexOf(key)
    return index === -1 ? order.length : index
  }
  return [...filtered, ...extras].sort(
    (a, b) => orderIndex(a.value) - orderIndex(b.value),
  )
}

/**
 * Detects synthetic conditions (blank, notBlank, inRange, notEqual, etc.)
 * from the stored filter value shape. Returns the condition key or undefined.
 */
export const detectSyntheticCondition = (
  value: Record<string, unknown>,
  filterTypeName: string | undefined,
  dateConditionsInDropdown: boolean,
): string | undefined => {
  const not = value.not as Record<string, unknown> | undefined

  if (value.gte != null && value.lte != null) return 'inRange'
  if (value.equals === null) return 'blank'
  if (not?.equals === null) return 'notBlank'
  // CubeStringFilter / CubeDateTimeFilter: blank/present use `set`
  if (value.set === false) return 'blank'
  if (value.set === true) return 'notBlank'

  switch (filterTypeName) {
    case 'DateTimeFilterInput': {
      if (dateConditionsInDropdown) {
        if (value.lt != null) return 'lessThan'
        if (value.gt != null) return 'greaterThan'
        if (not?.equals != null && typeof not.equals === 'string')
          return 'notEqual'
      }
      if (
        value.lt != null ||
        value.gt != null ||
        not?.equals != null ||
        value.equals != null
      )
        return 'equals'
      break
    }
    case 'CubeDateTimeFilter': {
      const inDateRange = value.inDateRange as unknown
      if (Array.isArray(inDateRange) && inDateRange[0] != null) {
        return 'inRange'
      }
      break
    }
    case 'IntFilterInput':
    case 'FloatFilterInput':
      if (not?.equals != null && typeof not.equals === 'number')
        return 'notEqual'
      break
    case 'StringFilterInput':
      if (not?.contains !== undefined) return 'notContains'
      if (not?.equals != null) return 'notEqual'
      break
    case 'CubeStringFilter':
      if (value.notContains !== undefined) return 'notContains'
      if (value.notEquals !== undefined) return 'notEqual'
      if (not?.contains !== undefined) return 'notContains'
      if (not?.equals != null) return 'notEqual'
      break
  }

  return undefined
}
